<template>
  <v-row class="filters--list mb-4">
    <v-col cols="12" sm="12" md="6" lg="4">
      <FilterSearch :fetchData="fetchHosts" />
    </v-col>

    <v-col cols="12" sm="12" md="4" lg="2">
      <FilterAutocomplete
        :label="$t('filters.status')"
        :fetchData="fetchHosts"
        :items="status"
        type="status"
      />
    </v-col>

    <v-col cols="12" sm="12" md="4" lg="2">
      <FilterAutocomplete
        :label="$t('filters.orderBy')"
        :fetchData="fetchHosts"
        :items="orderBy"
        type="orderBy"
      />
    </v-col>

    <v-col cols="12" sm="12" md="4" lg="2">
      <FilterAutocomplete
        :label="$t('filters.country')"
        :fetchData="fetchHosts"
        :items="countries"
        type="countryCode"
        itemText="text"
        value="value"
        @filterSetted="handleCountryFilterSetted"
      />
    </v-col>

    <v-col v-if="showStateFilter" cols="12" sm="12" md="4" lg="2">
      <FilterAutocomplete
        :key="filters.countryCode"
        :label="$t('filters.state')"
        :fetchData="fetchHosts"
        :items="states"
        type="stateId"
        value="id"
      />
    </v-col>

    <v-col cols="12" sm="12" md="4" lg="2">
      <FilterCity
        :fetchData="fetchHosts"
        :multiple="true"
        :countryCode="filters.countryCode"
        :stateId="filters.stateId"
      />
    </v-col>

    <v-col cols="12" sm="12" md="4" lg="3">
      <FilterAutocomplete
        :label="$t('filters.specialties')"
        :fetchData="fetchHosts"
        :items="specialties"
        type="specialties"
        itemText="name"
        value="id"
        multiple
      />
    </v-col>

    <v-col cols="12" sm="12" md="4" lg="3">
      <FilterAutocomplete
        :label="$t('filters.serviceTypes')"
        :fetchData="fetchHosts"
        :items="serviceTypes"
        :item-text="(item) => $t(`type.${item.value}`)"
        type="services"
        multiple
      />
    </v-col>

    <v-col cols="12" sm="12" md="4" lg="2">
      <FilterAutocomplete
        :label="$t('filters.hostOnboardingStep')"
        :fetchData="fetchHosts"
        :items="formattedHostOnboardingStepTypes"
        type="hostOnboardingStepType"
      />
    </v-col>

    <v-col cols="12" sm="12" md="4" lg="3">
      <FilterDates
        singleDateProp="startedOnboardingTrainingAt"
        :label="$t('filters.startedOnboardingTrainingAt')"
        :fetchData="fetchHosts"
        :singleDate="true"
      />
    </v-col>

    <v-col cols="12" sm="12" md="4" lg="2">
      <FilterBoolean
        :fetchData="fetchHosts"
        :label="$t('filters.starred')"
        type="starred"
      />
    </v-col>

    <v-col cols="12" sm="12" md="4" lg="2">
      <FilterBoolean
        :fetchData="fetchHosts"
        :label="$t('filters.monitored')"
        type="monitored"
      />
    </v-col>

    <v-col cols="12" sm="12" md="4" lg="3">
      <FilterBoolean
        :fetchData="fetchHosts"
        :label="$t('host.label.serviceProvider')"
        type="withServiceProvider"
      />
    </v-col>

    <v-col v-if="showCoursePercentageFilter" cols="12" sm="12" md="4" lg="3">
      <FilterAutocomplete
        :label="$t('filters.hostOnboardingCoursePercentage')"
        :fetchData="fetchHosts"
        :items="formattedHostOnboardingCoursePercentages"
        type="coursePercentage"
      />
    </v-col>
  </v-row>
</template>

<script>
import FilterDates from '@/components/filters/FilterDates'
import FilterSearch from '@/components/filters/FilterSearch'
import FilterBoolean from '@/components/filters/FilterBoolean'
import FilterAutocomplete from '@/components/filters/FilterAutocomplete'
import FilterCity from '@/components/filters/FilterCity'

import { countriesList } from '../../util/enums'

import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'AppointmentListFilters',

  components: {
    FilterAutocomplete,
    FilterBoolean,
    FilterSearch,
    FilterDates,
    FilterCity,
  },

  computed: {
    ...mapGetters([
      'cities',
      'filters',
      'isLoading',
      'serviceTypes',
      'hostOnboardingStepTypes',
      'hostOnboardingCoursePercentages',
      'isCitiesLoading',
      'specialties',
      'states',
    ]),

    formattedHostOnboardingStepTypes() {
      return this.hostOnboardingStepTypes.map((item) => ({
        name: this.$t(`hostOnboarding.types.${item}`),
        value: item,
      }))
    },

    formattedHostOnboardingCoursePercentages() {
      return this.hostOnboardingCoursePercentages.map((item) => ({
        name: this.$t(`hostOnboarding.coursePercentage.${item}`),
        value: item,
      }))
    },

    showCoursePercentageFilter() {
      if (!this.filters.hostOnboardingStepType) {
        if (this.filters.coursePercentage) this.filters.coursePercentage = null
        return false
      }
      return this.filters.hostOnboardingStepType === 'COURSE'
    },

    showStateFilter() {
      if (!this.filters.countryCode) {
        if (this.filters.stateId) this.filters.stateId = null
        return false
      }
      return true
    },
  },

  methods: {
    ...mapActions([
      'fetchHosts',
      'fetchCitiesPaginated',
      'fetchServiceTypes',
      'fetchHostOnboardingStepTypes',
      'fetchSpecialties',
      'fetchStates',
    ]),

    handleCountryFilterSetted() {
      this.filters.stateId = null
      this.fetchCitiesPaginated()
      this.fetchStates({ countryCode: this.filters.countryCode })
    },
  },

  data() {
    return {
      orderBy: [
        { value: 'ASC', name: this.$t('filters.older') },
        { value: 'DESC', name: this.$t('filters.newer') },
      ],
      status: [
        { value: 'ACTIVE', name: this.$t('host.status.ACTIVE') },
        { value: 'BLOCKED', name: this.$t('host.status.BLOCKED') },
        { value: 'SUSPENDED', name: this.$t('host.status.SUSPENDED') },
      ],
      selectedStep: null,
      countries: countriesList.map((country) => ({
        text: `${country.flag} ${country.country}`,
        value: country.value,
      })),
    }
  },

  async mounted() {
    this.fetchServiceTypes()
    this.fetchHostOnboardingStepTypes()
    this.fetchSpecialties({ search: '' })
  },
}
</script>
